import type { FeatureLockRegistry, LockCondition } from './types';

/**
 * Standard bypass conditions applied to most features.
 * - Praetorian users: Internal operators who need full access for testing
 * - Demo organizations: Showcase accounts that need feature visibility
 *
 * Use this for most features. Create custom bypasses only for
 * features with specific access requirements.
 *
 * Any feature with these bypasses will unlock for internal/demo access
 * regardless of flag or subscription state.
 */
export const STANDARD_BYPASSES: LockCondition[] = [
  { type: 'userRole', value: 'praetorian' },
  { type: 'orgType', value: 'demo' },
];

/**
 * Centralized Feature Lock Configuration
 *
 * This registry defines all feature lock conditions in a single location.
 * Each feature ID maps to a configuration specifying:
 * - conditions: What must be true to UNLOCK the feature
 * - operator: How to combine multiple conditions (AND/OR)
 * - lockTitle/lockDescription: Custom messaging when locked
 *
 * Condition evaluation rules:
 * - AND: ALL conditions must pass to UNLOCK
 * - OR: ANY condition passing will UNLOCK
 * - negate: Inverts the condition (useful for "disable" flags)
 *
 * @example
 * // Simple flag check
 * 'myFeature': {
 *   conditions: { type: 'flag', value: 'enableMyFeature' },
 * }
 *
 * @example
 * // Multiple conditions with OR
 * 'adminFeature': {
 *   conditions: [
 *     { type: 'userRole', value: 'praetorian' },
 *     { type: 'flag', value: 'enableAdminAccess' },
 *   ],
 *   operator: 'OR',
 * }
 */
export const featureLockConfig: FeatureLockRegistry = {
  // ============================================
  // AGENTS
  // ============================================

  /**
   * Agents section - requires breach and attack flag
   */
  agents: {
    conditions: { type: 'flag', value: 'enableBreachAndAttack' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Agents Locked',
    lockDescription: 'Enable the Intranet attack surface to view agents.',
    featureName: 'Agents',
  },

  // ============================================
  // AI ASSISTANT
  // ============================================

  /**
   * AI Assistant - locked when AI/ML training is disabled
   * Note: Uses negate because the flag DISABLES the feature
   */
  aiAssistant: {
    conditions: { type: 'flag', value: 'disableAIInference', negate: true },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'AI Assistant Disabled',
    lockDescription: 'AI inference has been disabled for this account.',
    featureName: 'AI Assistant',
  },

  // ============================================
  // ATTACK PATHS
  // ============================================

  /**
   * Attack Paths - requires breach and attack flag
   * Accessible to customers with flag enabled, or via standard bypasses
   */
  attackPaths: {
    conditions: { type: 'flag', value: 'enableBreachAndAttack' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Attack Paths Locked',
    lockDescription:
      'Contact support@praetorian.com to enable attack path analysis.',
    featureName: 'Attack Paths',
  },

  // ============================================
  // SEEDS - WEB APPLICATIONS TAB
  // ============================================

  /**
   * Seeds Web Applications tab - requires application attack surface
   * Both conditions must be met: applications enabled AND numberOfApplications > 0
   */
  'seeds.webApplications': {
    conditions: [
      { type: 'subscription', value: 'attackSurface.applications' },
      { type: 'subscription', value: 'numberOfApplications' },
    ],
    operator: 'AND',
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Web Applications Locked',
    lockDescription:
      'Enable the Applications attack surface to manage web application seeds.',
    featureName: 'Web Applications',
  },

  // ============================================
  // VALIDATIONS TABS
  // ============================================

  /**
   * Validations Overview tab - requires intranet attack surface
   */
  'validations.overview': {
    conditions: { type: 'subscription', value: 'attackSurface.internal' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Overview Locked',
    lockDescription: 'Enable the Intranet attack surface to view overview.',
    featureName: 'Validations Overview',
  },

  /**
   * Validations Attack tab - requires intranet attack surface
   */
  'validations.attack': {
    conditions: { type: 'subscription', value: 'attackSurface.internal' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Attack Tab Locked',
    lockDescription:
      'Enable the Intranet attack surface to view attack validations.',
    featureName: 'Validations Attack',
  },

  /**
   * Validations Defense tab - requires intranet attack surface
   */
  'validations.defense': {
    conditions: { type: 'subscription', value: 'attackSurface.internal' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Defense Tab Locked',
    lockDescription:
      'Enable the Intranet attack surface to view defense validations.',
    featureName: 'Validations Defense',
  },

  /**
   * Validations EMB3D tab - requires device attack surface
   */
  'validations.emb3d': {
    conditions: { type: 'subscription', value: 'attackSurface.device' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'EMB3D Tab Locked',
    lockDescription:
      'Enable the Device attack surface to view EMB3D validations.',
    featureName: 'Validations EMB3D',
  },

  /**
   * Validations Gaps tab - requires intranet attack surface
   */
  'validations.gaps': {
    conditions: { type: 'subscription', value: 'attackSurface.internal' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Gaps Tab Locked',
    lockDescription: 'Enable the Intranet attack surface to view gaps.',
    featureName: 'Validations Gaps',
  },

  /**
   * Validations Sessions tab - requires breach & attack flag + Praetorian
   * This is a Praetorian-only feature for BAS validation sessions.
   */
  'validations.sessions': {
    conditions: [
      { type: 'flag', value: 'enableBreachAndAttack' },
      { type: 'userRole', value: 'praetorian' },
    ],
    operator: 'AND',
    lockTitle: 'Validation Sessions Locked',
    lockDescription:
      'Breach & Attack Validation sessions are available for Praetorian operators.',
    featureName: 'Breach & Attack Validation',
  },

  // ============================================
  // CLOUD FEATURES
  // ============================================

  /**
   * Cloud integration features
   */
  cloud: {
    conditions: { type: 'flag', value: 'enableCloud' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Cloud Features Locked',
    lockDescription: 'Contact support@praetorian.com to enable cloud scanning.',
    featureName: 'Cloud',
  },

  // ============================================
  // CODE SCANNING
  // ============================================

  /**
   * Code scanning features
   */
  codeScanning: {
    conditions: { type: 'flag', value: 'enableCode' },
    bypasses: STANDARD_BYPASSES,
    lockTitle: 'Code Scanning Locked',
    lockDescription: 'Contact support@praetorian.com to enable code scanning.',
    featureName: 'Code Scanning',
  },
};
